import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import SubscriptionModal from '../components/SubscriptionModal';
import type { UpgradeHighlightReason } from '../types';

export type { UpgradeHighlightReason };

interface UpgradeModalContextValue {
  showUpgrade: (reason: UpgradeHighlightReason) => void;
}

const UpgradeModalContext = createContext<UpgradeModalContextValue | undefined>(
  undefined
);

export function UpgradeModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState<UpgradeHighlightReason | null>(null);

  const showUpgrade = useCallback((reason: UpgradeHighlightReason) => {
    setHighlight(reason);
    setOpen(true);
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
    setHighlight(null);
  }, []);

  return (
    <UpgradeModalContext.Provider value={{ showUpgrade }}>
      {children}
      <SubscriptionModal open={open} onClose={onClose} highlightReason={highlight} />
    </UpgradeModalContext.Provider>
  );
}

export function useUpgradeModal(): UpgradeModalContextValue {
  const ctx = useContext(UpgradeModalContext);
  if (!ctx) {
    throw new Error('useUpgradeModal must be used inside UpgradeModalProvider');
  }
  return ctx;
}
